import { CommunicationUserIdentifier } from '@azure/communication-common';
import {
    CallAdapterState,
    CallAgentProvider,
    CallClientProvider,
    CallWithChatCompositeOptions,
    ChatClientProvider,
    COMPONENT_LOCALE_DE_DE,
    FluentThemeProvider,
    LocalizationProvider,
} from '@azure/communication-react';
import { createTheme, PartialTheme, Theme } from '@fluentui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApplicationInsightsProvider, ReactInsightsConfig, withAITracking } from 'live-lesson/shared/util-insights-react';
import {
    CallAdapterProvider,
    CreatePcpSharedContextProps,
    createV9Theme,
    fillWidthAndHeightStyles,
    fullScreenWidthAndHeightStyles,
    hiddenOverflowStyles,
    LoadingPage,
    pcpComponentsLightTheme,
    pcpComponentsLightThemeV9,
    PcpLiveLessonStoreContextProvider,
    PcpSharedContextType,
    registerFluentUiIcons,
    TestCallWithChatComposite,
    useCallAdapterTelemetry,
    useIsMobile,
    useLiveLessonAppTracking,
    useObservable,
    usePcpCreateStatefulClients, useReactPdf,
} from "live-lesson/shared/feature-call-ui-react";
import { AppContext, SidePaneContentType } from './app-state/AppContext';
import { PageSwitcher } from './PageSwitcher';
import { FluentProvider, Theme as ThemeV9, webLightTheme } from '@fluentui/react-components';
import { Observable } from 'rxjs';
import { LiveSessionState } from '@pcp/core/interfaces';
import { GroupCallLocator, RoomCallLocator } from '@azure/communication-calling';
import { reactPlugin } from 'live-lesson/shared/util-insights-react';

useReactPdf();
registerFluentUiIcons();

export type AppProps = {
    // Props needed for the construction of the CallWithChatAdapter
    userId: CommunicationUserIdentifier;
    displayName: string;
    endpointUrl: string;
    locator: GroupCallLocator;
    chatThreadId: string;

    // TODO: use either token _or_ credential
    token: string;


    // Props to customize the CallWithChatComposite experience
    fluentTheme?: PartialTheme | Theme;
    rtl?: boolean;
    compositeOptions?: CallWithChatCompositeOptions;
    callInvitationURL?: string;
    formFactor?: 'desktop' | 'mobile';

    stateChangedCallback?: (state: CallAdapterState) => void;
    activeElementElement: HTMLElement | null;

    pcpCallState: Observable<LiveSessionState>;
    lessonId: string;
    pcpToken: string;

    apiGet: <TResponse, TRequest>(url: string, data?: TRequest) => Promise<TResponse>;
    apiPost: <TResponse, TRequest>(url: string, data: TRequest) => Promise<TResponse>;
    apiPostProgress:  PcpSharedContextType['apiPostProgress'];
    apiDelete: <TResponse, TRequest>(url: string, data?: TRequest) => Promise<TResponse>;

    // Application Insights configuration (passed from Angular)
    applicationInsightsConfig: ReactInsightsConfig;
};

// Inner App component that uses Application Insights
const AppWithInsights = (props: AppProps): JSX.Element => {

    const { callClient, callAgent, chatClient, callAdapter } = usePcpCreateStatefulClients({
        userId: props.userId,
        displayName: props.displayName,
        endpoint: props.endpointUrl,
        locator: props.locator,
        token: props.token,
        autoStartCamera: true,
    });

    const roomCallLocator: RoomCallLocator = useMemo(() => ({ roomId: props.locator.groupId}), [props.locator.groupId]);

    const pcpCallState = useObservable(props.pcpCallState);

    const [isSidePaneOpen, setIsSidePaneOpen] = useState(false);
    const [sidePaneContent, setSidePaneContent] = useState<SidePaneContentType[]>([]);


    const addSidePaneContent = useCallback((item: SidePaneContentType) => {
        if (!sidePaneContent.includes(item)) {
            setSidePaneContent([...sidePaneContent, item]);
        }
    }, [sidePaneContent]);

    const removeSidePaneContent = useCallback((item: SidePaneContentType) => {
        if (sidePaneContent.includes(item)) {
            setSidePaneContent(sidePaneContent.filter(content => content !== item));
        }
    }, [sidePaneContent]);

    useEffect(() => {
        if (sidePaneContent.length === 0) {
            setIsSidePaneOpen(false);
        } else {
            setIsSidePaneOpen(true);
        }
    }, [sidePaneContent]);

    const toggleSidePaneContent = useCallback((item: SidePaneContentType) => {
        if (sidePaneContent.includes(item)) {
            removeSidePaneContent(item);
        } else {
            addSidePaneContent(item);
        }
    }, [sidePaneContent]);

    const themeV9 = pcpComponentsLightThemeV9;
    const themeV8 = pcpComponentsLightTheme;

    const isMobile = useIsMobile();

    // Use new tracking hooks
    useCallAdapterTelemetry({
        callAdapter,
        lessonId: props.lessonId
    });

    // Handle state change callback separately
    useEffect(() => {
        if (!callAdapter || !props.stateChangedCallback) return;

        const callback = props.stateChangedCallback;
        callAdapter.onStateChange(callback);
        return () => {
            callAdapter.offStateChange(callback);
        };
    }, [callAdapter, props.stateChangedCallback]);

    useLiveLessonAppTracking({
        lessonId: props.lessonId,
        userId: props.userId.communicationUserId,
        displayName: props.displayName,
        appType: 'Moderator',
        isMobile,
        pcpCallState
    });

    const sharedContext = useMemo((): CreatePcpSharedContextProps => ({
        callAgent,
        callClient,
        chatClient,
        acsToken: props.token,
        activeElement: props.activeElementElement,
        pcpCallState$: props.pcpCallState,
        lessonId: props.lessonId,
        pcpToken: props.pcpToken,

        apiGet: props.apiGet,
        apiPost: props.apiPost,
        apiPostProgress: props.apiPostProgress,
        apiDelete: props.apiDelete,
    }), [
        callAgent,
        callClient,
        chatClient,
        props.token,
        props.activeElementElement,
        props.pcpCallState,
        props.lessonId,
        props.pcpToken,
    ]);
    const appContext = useMemo(() => ({
        isSidePaneOpen,
        setIsSidePaneOpen,
        sidePaneContent,
        addSidePaneContent,
        removeSidePaneContent,
        toggleSidePaneContent,
    }), [
        isSidePaneOpen,
        setIsSidePaneOpen,
        sidePaneContent,
        addSidePaneContent,
        removeSidePaneContent,
        toggleSidePaneContent,
    ]);

    // for testing: switch between composite and UI components
    const useComposite = localStorage.getItem('useComposite') === 'true';
    if (useComposite) {
        return <TestCallWithChatComposite
            // NOTE: chat won't work because of the missing chat thread id
            locator={{ callLocator: props.locator, chatThreadId: '' }}
            callAgent={callAgent}
            callClient={callClient}
            chatClient={chatClient}
            threadId={props.chatThreadId}
            callAdapter={callAdapter}
            isMobile={isMobile} />;
    }


    if (!callAdapter) {
        return <LoadingPage />;
    }

    return (
        <div style={fullScreenWidthAndHeightStyles}>
                <AppContext.Provider value={appContext}>
                    <FluentProvider theme={themeV9} style={fillWidthAndHeightStyles}>
                        <FluentThemeProvider fluentTheme={themeV8} rootStyle={hiddenOverflowStyles}>

                            <LocalizationProvider locale={COMPONENT_LOCALE_DE_DE}>
                                <CallAdapterProvider adapter={callAdapter}>
                                    {callClient && (
                                        <CallClientProvider callClient={callClient}>
                                            {chatClient && (
                                                <ChatClientProvider chatClient={chatClient}>
                                                        {callAgent && (
                                                            <CallAgentProvider callAgent={callAgent}>
                                                                <PcpLiveLessonStoreContextProvider
                                                                    callClient={callClient}
                                                                    chatClient={chatClient}
                                                                    sharedProps={sharedContext}
                                                                >
                                                                    <PageSwitcher
                                                                        callLocator={roomCallLocator}
                                                                        isMobile={isMobile}
                                                                    />
                                                                </PcpLiveLessonStoreContextProvider>
                                                            </CallAgentProvider>
                                                        )}
                                                </ChatClientProvider>
                                            )}
                                        </CallClientProvider>
                                    )}
                                </CallAdapterProvider>
                            </LocalizationProvider>
                        </FluentThemeProvider>
                    </FluentProvider>
                </AppContext.Provider>
        </div>
    );
};

// Main App component wrapped with Application Insights
const App = (props: AppProps): JSX.Element => {
    // Config comes from Angular. Without it the provider builds no SDK and ReactInsightsLogger
    // guards every method on it, so logging becomes a silent no-op — NOT console output.
    const aiConfig = props.applicationInsightsConfig;

    return (
        <ApplicationInsightsProvider config={aiConfig}>
            <AppWithInsights {...props} />
        </ApplicationInsightsProvider>
    );
};

export default withAITracking(reactPlugin, App);
